/*
 * Welcome-gift cart auto-add — Progurt canon
 * -------------------------------------------------------------------------
 * Keeps subscription welcome gifts visible before checkout for eligible or
 * unknown-signal carts, and removes them for known prior subscribers. Pricing
 * belongs to the unchanged ops welcome-gift Function and its validity rules;
 * this script only maintains marked lines, never changes their price.
 *
 * Rule (canonical — mirrors the Bridge worker's GIFT_RULES):
 *   SUBSCRIPTION line (selling_plan present) of SKU BDL-DAILY      → +1x PRG-INCUBATOR            (_gift: "daily")
 *   SUBSCRIPTION line (selling_plan present) of SKU BDL-DAILY-PLUS → +1x PRG-INCUBATOR
 *                                                                    +1x PRG-PROBIOTIC-5          (_gift: "daily-plus")
 *   Both doors share ONE Incubator per cart; Daily+ takes priority for its tag.
 *   One-time (no selling_plan) purchases of those SKUs get NO gift.
 *
 * Behaviour:
 *   1. Add gifts for eligible or unknown-signal qualifying subscriptions. Never
 *      duplicate (one Incubator across doors; other gifts dedupe on variant id
 *      + _gift tag; extra duplicates are pruned).
 *   2. Remove gift lines (identified by the _gift property) when the qualifying
 *      bundle leaves the cart or the rendered priorSubscription signal is true.
 *   3. A manually removed gift is re-added on the next cart interaction / page
 *      load only for eligible or unknown-signal carts. Identity is a render snapshot.
 *
 * Resilience (WS1a Stage-2 — publish-independent, after Bug A):
 *   The reconcile MUST fire on every cart mutation regardless of who wrote it or
 *   whether Dawn publishes cartUpdate. Bug A was "Dawn mutated the cart, then
 *   threw before publish(cartUpdate)", starving the reconcile and orphaning a $0
 *   gift line at full price. So we observe cart writes at the NETWORK layer:
 *     - our OWN cart writes go through `nativeFetch` (captured before wrapping),
 *       so they never re-enter our observer — no feedback loop, no over-suppression;
 *     - window.fetch + XMLHttpRequest are wrapped to schedule a debounced reconcile
 *       after ANY external /cart/(add|change|update|clear) completes (any status);
 *     - the cartUpdate subscription + DOMContentLoaded + pageshow remain as cheap
 *       secondary/fallback triggers (safe: reconcile is fresh, idempotent,
 *       single-flight and debounced).
 *   A failed gift removal retries ONCE with a fresh line key; if it still fails we
 *   show an alert where cart error containers exist, clearing it on the next
 *   clean reconcile. Checkout is not blocked; valid residual gifts retain the
 *   unchanged Function's discount eligibility.
 *
 * Config is injected by snippets/welcome-gift-config.liquid as
 * window.Progurt.welcomeGift (resolved per store). Feature-gated by the
 * `welcome_gift_enabled` theme setting; when off the config snippet renders
 * nothing and this file is never loaded.
 *
 * The `_gift` property key is a hard contract with the Bridge order-normalizer
 * (it keys gift-present detection on it). Never rename it.
 */
(function () {
  'use strict';

  window.Progurt = window.Progurt || {};

  var CFG = window.Progurt.welcomeGift;
  if (!CFG || !CFG.enabled || !CFG.rules || !Object.keys(CFG.rules).length) return;

  // Never mutate the cart inside the theme editor preview.
  if (window.Shopify && window.Shopify.designMode) return;

  var GIFT_PROP = CFG.giftProperty || '_gift';
  var routes = window.routes || {};

  // Native fetch captured BEFORE we wrap window.fetch — ALL of welcome-gift's own
  // reads and writes go through this, so our traffic never re-enters our observer
  // (the decisive "not our request" rule, stronger than a reconcile-in-flight flag).
  var nativeFetch = (window.fetch ? window.fetch.bind(window) : null);
  if (!nativeFetch) return; // no fetch → every read/write/observer relies on it; can't operate

  var isReconciling = false;
  var rerunQueued = false;

  // ── Cart reads ──────────────────────────────────────────────────────────
  function getCart() {
    return nativeFetch((routes.cart_url || '/cart') + '.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    }).then(function (r) {
      return r.json();
    });
  }

  // Gifts desired for this rendered identity and its qualifying subscriptions.
  // One Incubator across doors/plugs, tagged daily-plus whenever that door qualifies.
  // Other gifts dedupe on variant id + tag; never scale gifts by bundle quantity.
  function computeDesired(cart) {
    if (CFG.priorSubscription === true) return [];
    var desired = [];
    var seen = {};
    var incubator = null;
    (cart.items || []).forEach(function (item) {
      if (!item.selling_plan_allocation) return; // subscription lines only
      var sku = (item.sku || '').toUpperCase();
      var rule = CFG.rules[sku];
      if (!rule) return;
      rule.gifts.forEach(function (gift) {
        if (!gift || typeof gift.id !== 'number') return;
        if ((gift.sku || '').toUpperCase().indexOf('PRG-INCUBATOR') === 0) {
          if (!incubator) {
            incubator = { id: gift.id, tag: rule.tag };
            desired.push(incubator);
          } else if (rule.tag === 'daily-plus') {
            // Replace the lower door's choice regardless of cart line order.
            incubator.id = gift.id;
            incubator.tag = rule.tag;
          }
          return;
        }
        var key = gift.id + '|' + rule.tag;
        if (seen[key]) return;
        seen[key] = true;
        desired.push({ id: gift.id, tag: rule.tag });
      });
    });
    return desired;
  }

  // Gift lines currently in the cart (any line carrying the _gift property).
  function computePresent(cart) {
    var present = [];
    (cart.items || []).forEach(function (item) {
      var props = item.properties || {};
      var tag = props[GIFT_PROP];
      if (!tag) return;
      present.push({ key: item.key, id: item.variant_id, tag: tag, quantity: item.quantity });
    });
    return present;
  }

  // ── Cart writes (own — via nativeFetch, invisible to our observer) ────────
  function changeLineQuantity(key, quantity) {
    return nativeFetch(routes.cart_change_url || '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    }).then(function (r) {
      // /cart/change.js returns the cart on success, or {status,message,description} on error.
      return r.json().then(
        function (d) { return { ok: r.ok && !d.status && !d.errors, data: d }; },
        function () { return { ok: r.ok, data: null }; }
      );
    }, function () {
      return { ok: false, data: null };
    });
  }

  function addGifts(gifts) {
    var items = gifts.map(function (g) {
      var props = {};
      props[GIFT_PROP] = g.tag;
      return { id: g.id, quantity: 1, properties: props };
    });
    return nativeFetch(routes.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    }).then(function (r) {
      return r.json();
    }).then(function (data) {
      // A sold-out / unavailable gift returns an error object — log, never throw
      // (the existing server-side backstop does not guarantee gift repair).
      if (data && data.status) {
        console.warn('[welcome-gift] gift add rejected:', data.description || data.message);
      }
      return data;
    });
  }

  // Remove a set of gift lines, each retried ONCE with a fresh line key if the
  // first attempt fails (the key can go stale after a concurrent cart write).
  // Returns a promise resolving to `true` if any removal ultimately failed.
  function doRemovals(lines, desiredKeys) {
    var anyFailed = false;
    var chain = Promise.resolve();
    lines.forEach(function (line) {
      chain = chain.then(function () {
        return changeLineQuantity(line.key, 0).then(function (res) {
          if (res.ok) return;
          // Retry with a fresh key — but ONLY if the cart genuinely still has an EXCESS of this
          // gift (present count > desired count). This guarantees a stale-key failure can never
          // make us remove the one line we mean to KEEP when duplicates race.
          return getCart().then(function (cart) {
            var desiredCount = desiredKeys[line.id + '|' + line.tag] ? 1 : 0;
            var matches = computePresent(cart).filter(function (p) {
              return p.id === line.id && p.tag === line.tag;
            });
            if (matches.length <= desiredCount) return; // no excess left → failure self-resolved
            return changeLineQuantity(matches[matches.length - 1].key, 0).then(function (r2) {
              if (!r2.ok) anyFailed = true;
            });
          });
        }).catch(function () { anyFailed = true; });
      });
    });
    return chain.then(function () { return anyFailed; });
  }

  // ── Removal-failure alert (where cart error containers exist) ─────────────
  var ERROR_TARGETS = ['CartDrawer-CartErrors', 'cart-errors'];
  var ERROR_MSG =
    'We couldn’t remove a free gift from your cart. Please refresh the page or remove it before checkout.';

  // Render our OWN alert node ADJACENT to Dawn's error container rather than writing into it,
  // so we can never overwrite or wipe Dawn's own error text (and Dawn can never wipe ours).
  function showGiftError() {
    ERROR_TARGETS.forEach(function (id) {
      var container = document.getElementById(id);
      if (!container) return;
      var nodeId = 'wg-gift-error--' + id;
      var node = document.getElementById(nodeId);
      if (!node) {
        node = document.createElement('div');
        node.id = nodeId;
        node.className = 'wg-cart-error';
        node.setAttribute('role', 'alert');
        node.setAttribute('data-wg-error', '1');
        node.style.cssText = 'color:#B00020;font-size:1.2rem;line-height:1.4;padding:0.6rem 0;';
        container.insertAdjacentElement('afterend', node);
      }
      node.textContent = ERROR_MSG;
    });
  }

  // Remove ONLY our own alert nodes (marked data-wg-error) — never touch Dawn's containers.
  function clearGiftError() {
    var nodes = document.querySelectorAll('.wg-cart-error[data-wg-error]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].parentNode) nodes[i].parentNode.removeChild(nodes[i]);
    }
  }

  // ── Section refresh (mirrors the theme's Section Rendering pattern) ───────
  function fetchSection(sectionId) {
    return nativeFetch((routes.cart_url || '/cart') + '?section_id=' + encodeURIComponent(sectionId))
      .then(function (r) { return r.text(); })
      .then(function (t) { return new DOMParser().parseFromString(t, 'text/html'); });
  }

  function refreshSections() {
    var tasks = [];

    var drawer = document.querySelector('cart-drawer');
    if (drawer) {
      tasks.push(fetchSection('cart-drawer').then(function (doc) {
        var src = doc.querySelector('.drawer__inner');
        var dst = drawer.querySelector('.drawer__inner');
        if (src && dst) dst.replaceWith(src);
      }));
    }

    var mainItems = document.getElementById('main-cart-items');
    if (mainItems && mainItems.dataset.id) {
      tasks.push(fetchSection(mainItems.dataset.id).then(function (doc) {
        var src = doc.querySelector('#main-cart-items .js-contents') || doc.querySelector('.js-contents');
        var dst = document.querySelector('#main-cart-items .js-contents');
        if (src && dst) dst.innerHTML = src.innerHTML;
      }));
    }

    var mainFooter = document.getElementById('main-cart-footer');
    if (mainFooter && mainFooter.dataset.id) {
      tasks.push(fetchSection(mainFooter.dataset.id).then(function (doc) {
        var src = doc.querySelector('#main-cart-footer .js-contents') || doc.querySelector('.js-contents');
        var dst = document.querySelector('#main-cart-footer .js-contents');
        if (src && dst) dst.innerHTML = src.innerHTML;
      }));
    }

    tasks.push(fetchSection('cart-icon-bubble').then(function (doc) {
      var src = doc.querySelector('.shopify-section') || doc.body.firstElementChild;
      var dst = document.getElementById('cart-icon-bubble');
      if (src && dst) dst.innerHTML = src.innerHTML;
    }));

    return Promise.all(tasks).catch(function (e) {
      console.error('[welcome-gift] section refresh failed', e);
    });
  }

  // ── Reconcile ────────────────────────────────────────────────────────────
  function reconcile() {
    if (isReconciling) { rerunQueued = true; return Promise.resolve(); }
    isReconciling = true;

    return getCart().then(function (cart) {
      var desired = computeDesired(cart);
      var present = computePresent(cart);

      // Keep exactly one present line per desired key; remove unwanted lines AND
      // duplicate extras of a wanted gift; add any desired gift not present at all.
      var desiredKeys = {};
      desired.forEach(function (d) { desiredKeys[d.id + '|' + d.tag] = true; });

      var kept = {};
      var toRemove = [];
      var toNormalize = [];
      present.forEach(function (p) {
        var k = p.id + '|' + p.tag;
        if (!desiredKeys[k]) { toRemove.push(p); return; } // not wanted
        if (kept[k]) { toRemove.push(p); return; }         // duplicate of a wanted gift
        kept[k] = true;                                    // keep the first
        if (p.quantity > 1) toNormalize.push(p);
      });
      var toAdd = desired.filter(function (d) { return !kept[d.id + '|' + d.tag]; });

      if (!toAdd.length && !toRemove.length && !toNormalize.length) { clearGiftError(); return null; }

      // Remove extras, normalize kept quantities, then confirm removals before adding.
      return doRemovals(toRemove, desiredKeys).then(function (removalFailed) {
        var chain = Promise.resolve();
        toNormalize.forEach(function (line) {
          chain = chain.then(function () {
            return changeLineQuantity(line.key, 1).then(function (res) {
              if (!res.ok) removalFailed = true;
            });
          });
        });
        if (toRemove.length && toAdd.length) chain = chain.then(getCart).then(function (freshCart) {
          var remaining = computePresent(freshCart);
          if (remaining.some(function (p) { return !desiredKeys[p.id + '|' + p.tag]; })) removalFailed = true;
          // A successful response alone does not prove the old Incubator is gone.
          // Also avoid merging with a gift that arrived during the removals.
          toAdd = toAdd.filter(function (d) {
            return !remaining.some(function (p) { return p.id === d.id && p.tag === d.tag; });
          });
        });
        chain = chain.then(function () {
          if (!removalFailed && toAdd.length) return addGifts(toAdd);
        });
        // Verify the result once; a failed repair waits for the next normal trigger.
        return chain
          .then(getCart)
          .then(function (freshCart) {
            var remaining = computePresent(freshCart);
            var verified = {};
            if (remaining.length !== desired.length || remaining.some(function (p) {
              var k = p.id + '|' + p.tag;
              if (!desiredKeys[k] || verified[k] || p.quantity !== 1) return true;
              verified[k] = true;
              return false;
            })) removalFailed = true;
          })
          .catch(function (e) {
            removalFailed = true;
            console.error('[welcome-gift] gift repair failed', e);
          })
          .then(refreshSections)
          .catch(function (e) { console.error('[welcome-gift] refresh failed', e); })
          .then(function () {
            if (removalFailed) showGiftError(); else clearGiftError();
          });
      });
    }).catch(function (e) {
      console.error('[welcome-gift] reconcile failed', e);
    }).then(function () {
      isReconciling = false;
      if (rerunQueued) {
        rerunQueued = false;
        return reconcile();
      }
    });
  }

  // ── Debounced trigger ─────────────────────────────────────────────────────
  var reconcileTimer = null;
  function scheduleReconcile() {
    if (reconcileTimer) clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(function () {
      reconcileTimer = null;
      reconcile();
    }, 150);
  }

  // ── Network observers (publish-independent) ───────────────────────────────
  // Wrap window.fetch + XMLHttpRequest to schedule a reconcile after ANY cart-write
  // completes, from any writer. Our own writes use nativeFetch and never reach here.
  var CART_WRITE_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|#|$)/;

  function installObservers() {
    if (window.fetch) {
      var wrappedFetch = window.fetch;
      window.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : (input && (input.url || input.href)) || '';
        var p = wrappedFetch.apply(this, arguments);
        // Fire regardless of resolve/reject or HTTP status — the reconcile re-reads /cart.js
        // and lets fresh state decide the truth.
        if (CART_WRITE_RE.test(url)) { p.then(scheduleReconcile, scheduleReconcile); }
        return p;
      };
    }

    try {
      var XO = XMLHttpRequest.prototype.open;
      var XS = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function (method, url) {
        this.__wgCartWrite = CART_WRITE_RE.test(url || '');
        return XO.apply(this, arguments);
      };
      XMLHttpRequest.prototype.send = function () {
        if (this.__wgCartWrite) { this.addEventListener('loadend', scheduleReconcile); }
        return XS.apply(this, arguments);
      };
    } catch (e) { /* XHR unavailable — fetch + fallbacks still cover it */ }
  }

  // ── Triggers ─────────────────────────────────────────────────────────────
  // Install the network observers immediately (script is `defer`, so this runs before
  // DOMContentLoaded), then the fresh boot reconcile repairs any write that landed
  // before install (e.g. a prior-session cart, or a very early add).
  installObservers();

  // Secondary trigger: Dawn's cartUpdate (kept — cheap, source-guarded, harmless
  // duplicate given debounce + single-flight + idempotent reconcile).
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.source === 'welcome-gift') return;
      scheduleReconcile();
    });
  }

  // Boot on load (qualifying bundle already in the cart from a prior session, hard
  // refresh, or direct /cart visit) + pageshow (bfcache restore / Recharge full-nav return).
  function boot() { reconcile(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  window.addEventListener('pageshow', function (e) { if (e.persisted) reconcile(); });
  // One more pass after full load catches a cart write that was in-flight before our observers
  // installed (started by an earlier script, completed after the boot reconcile's snapshot).
  window.addEventListener('load', function () { reconcile(); });
  // Re-check when the tab regains focus — cheap safety net for anything the network observers
  // could miss (a pre-install in-flight write, or a rare non-fetch/non-XHR app cart mutation).
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') scheduleReconcile();
  });

  window.Progurt.WelcomeGift = { reconcile: reconcile };
})();
